import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { readUrlParams } from "../lib/urlParams.js";
import { fullTimestampFormatter } from "../lib/traceUtils.js";
import { previewTraceCanvas } from "../lib/traceCanvasStore.js";
import ReadingSwitcherGroup from "./ReadingSwitcherGroup.jsx";
import { CONTEXT_EYEBROW_CLASS } from "./SplitPage.jsx";
import { useDetailHeaderSlot } from "./detailHeaderSlot.js";

// ── The trace panel's placeholder while getTrace is in flight ─────────────
//
// A placeholder has to stand in for the panel it is replacing, section for
// section, so nothing moves when the payload lands: the reading switcher on
// the detail header's line (or heading the reading where the header offers no
// slot), the summary line, the review row, the metadata rail, the reading's
// bar and its outline. The reading is URL state (`hist`), so an operator
// arrives on a deep link (or reopens the panel where they left it) with X-ray
// or Raw already selected; each reading contributes its own outline below,
// off the same param the loaded panel reads.
//
// Two rules keep the outline honest, because a placeholder that promises more
// than arrives is worse than a vague one:
//   1. Only sections that ALWAYS render get a real label. Sections that render
//      conditionally on the payload (related work, the record's notices) are
//      left out, and a row whose words depend on the payload is an
//      unlabelled block.
//   2. Nothing is invented. Bars stand for text whose width is unknown.

// Widths/heights are literal pixels so the bars line up with the type they
// stand in for; opacity carries the hierarchy (see the no-gradients rule).
function Bar({ w = "100%", h = 12, dim = 0.7, className = "" }) {
  return (
    <div
      aria-hidden="true"
      className={`rounded-[1px] bg-[color:var(--line-soft)] ${className}`}
      style={{ width: typeof w === "number" ? `${w}px` : w, height: h, opacity: dim }}
    />
  );
}

// The lanes rail mirrors TraceLanesView's own geometry (240px rail, 24px rows)
// so the grid does not resize under the reader when the payload lands.
const RAIL_W = 240;
const ROW_H = 24;

// depth = the rail's group nesting (band → folder → lane), so the outline reads
// as a tree of subjects rather than a flat list.
const LANE_RAIL_ROWS = [
  { depth: 0, w: 108, head: true },
  { depth: 1, w: 64 },
  { depth: 1, w: 72 },
  { depth: 1, w: 96 },
  { depth: 0, w: 84, head: true },
  { depth: 1, w: 128 },
  { depth: 1, w: 112 },
  { depth: 0, w: 72, head: true },
  { depth: 1, w: 140 },
  { depth: 2, w: 116 },
  { depth: 2, w: 132 },
  { depth: 2, w: 104 },
  { depth: 2, w: 148 },
  { depth: 1, w: 96 },
  { depth: 2, w: 124 },
  { depth: 2, w: 88 },
];

const RAW_LINES = [
  { indent: 0, w: "18%" },
  { indent: 12, w: "62%" },
  { indent: 24, w: "44%" },
  { indent: 24, w: "71%" },
  { indent: 12, w: "33%" },
  { indent: 12, w: "56%" },
  { indent: 24, w: "78%" },
  { indent: 36, w: "49%" },
  { indent: 36, w: "64%" },
  { indent: 24, w: "27%" },
  { indent: 12, w: "58%" },
  { indent: 24, w: "69%" },
  { indent: 24, w: "41%" },
  { indent: 12, w: "22%" },
  { indent: 12, w: "47%" },
  { indent: 24, w: "74%" },
  { indent: 36, w: "52%" },
  { indent: 36, w: "38%" },
  { indent: 24, w: "61%" },
  { indent: 12, w: "29%" },
  { indent: 0, w: "12%" },
];

// The metadata rail's rows, as tall as the loaded rows they stand for: the
// time, the identity chips, the nearby-work bar and its legend, the model,
// the tools called (two lines).
const META_ROWS = [
  { label: 34, lines: [{ w: "28%", h: 12 }] },
  { label: 52, lines: [{ w: "64%", h: 20 }] },
  { label: 46, lines: [{ w: "100%", h: 10 }, { w: "58%", h: 9, dim: 0.4 }, { w: "52%", h: 9, dim: 0.4 }, { w: "61%", h: 9, dim: 0.4 }] },
  { label: 44, lines: [{ w: "34%", h: 12 }] },
  { label: 40, lines: [{ w: "86%", h: 12 }, { w: "38%", h: 12 }] },
];

function LoadingNote({ className = "" }) {
  return (
    <span className={`inline-flex items-center gap-[5px] ${className}`} aria-live="polite">
      <span className="h-[6px] w-[6px] animate-pulse rounded-full bg-[color:var(--green)]" aria-hidden="true" />
      Loading trace details
    </span>
  );
}

// A collapsed turn: chevron, role, block count, and a two-line preview clamp.
function TurnCard({ preview }) {
  return (
    <div data-turn-card="collapsed" className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[6px] min-w-0">
      <div className="grid gap-[4px]">
        <div className="flex items-center gap-[6px]">
          <Bar w={8} h={8} dim={0.45} />
          <Bar w={52} h={9} dim={0.7} />
          <Bar w={38} h={8} dim={0.35} />
        </div>
        <div className="grid gap-[3px] pl-[18px]">
          <Bar w={preview[0]} h={9} dim={0.4} />
          <Bar w={preview[1]} h={9} dim={0.3} />
        </div>
      </div>
    </div>
  );
}

// The newest turn opens by default — it is the prompt the trace exists for — so
// the list is one open turn among collapsed ones, and `ord` decides which end it
// sits at. Anything else would move the reader's turn as the payload lands.
function TurnCards({ newestFirst }) {
  const collapsed = [["92%", "68%"], ["78%", "44%"], ["86%", "57%"], ["61%", "38%"]];
  const open = (
    <div key="open" data-turn-card="open" className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] min-w-0">
      <div className="grid gap-[4px] px-[8px] py-[6px]">
        <div className="flex items-center gap-[6px]">
          <Bar w={8} h={8} dim={0.6} />
          <Bar w={52} h={9} dim={0.7} />
          <Bar w={38} h={8} dim={0.35} />
        </div>
      </div>
      {/* The open turn's blocks, each its own labelled mini-panel. */}
      <div className="grid divide-y divide-[color:var(--line)] border-t border-[color:var(--line)] px-[8px]">
        {[["94%", "88%", "62%"], ["72%", "46%"]].map((lines, i) => (
          <div key={i} className="grid gap-[5px] py-[10px]">
            <Bar w={64} h={8} dim={0.5} />
            {lines.map((w, j) => (
              <Bar key={j} w={w} h={10} dim={0.4 - j * 0.06} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
  const rest = collapsed.map((preview, i) => <TurnCard key={i} preview={preview} />);
  return (
    <div className="grid gap-[4px] min-w-0">
      {newestFirst ? [open, ...rest] : [...rest, open]}
    </div>
  );
}

// The X-ray feed: a coverage score, then dated rungs of classified operations.
function WorkOutline() {
  return (
    <div className="grid gap-[6px] min-w-0">
      <div className="flex items-center gap-[8px] border-b border-[color:var(--line-soft)] pb-[4px]">
        <Bar w={168} h={9} dim={0.5} />
        <Bar w={96} h={9} dim={0.3} />
      </div>
      {[["84%", "62%", "73%", "46%"], ["57%", "88%", "64%"], ["79%", "51%", "68%"]].map((rows, group) => (
        <div key={group} className="grid gap-[3px] min-w-0">
          {/* The dated rung each run of operations hangs from. */}
          <div className="flex items-center gap-[6px]">
            <Bar w={40} h={8} dim={0.45} />
            <div aria-hidden="true" className="h-[1px] flex-1 bg-[color:var(--line-soft)]" style={{ opacity: 0.6 }} />
          </div>
          {rows.map((w, i) => (
            <div key={i} className="flex items-baseline gap-[9px] px-[9px] py-[2px] min-w-0">
              <Bar w={62} h={9} dim={0.5} className="shrink-0" />
              <Bar w={w} h={10} dim={0.4} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// The lanes plane: sticky subject rail, turn axis, and the empty cell field
// between them. No marks — see the honesty rules at the top of this file.
function LanesOutline() {
  return (
    <div className="grid min-w-0 gap-[6px]">
      <div className={`flex min-w-0 flex-wrap items-center gap-x-[10px] gap-y-[3px]`}>
        <LoadingNote className="text-[11px] leading-[1.3] text-[color:var(--muted)]" />
        <Bar w={148} h={9} dim={0.4} />
        <Bar w={116} h={9} dim={0.4} />
        <span className="min-w-0 flex-1" />
        {[58, 54, 48, 62, 70].map((w, i) => (
          <Bar key={i} w={w} h={9} dim={0.3} />
        ))}
      </div>
      <div className="overflow-hidden rounded-[2px] border border-[color:var(--line-soft)] min-w-0">
        <div
          className="flex items-center border-b border-[color:var(--line-soft)]"
          style={{ height: ROW_H }}
        >
          <div
            className="flex shrink-0 items-center border-r border-[color:var(--line-soft)] px-[8px]"
            style={{ width: RAIL_W, height: ROW_H }}
          >
            <Bar w={96} h={9} dim={0.5} />
          </div>
          {/* The turn axis: its ruler, since tick POSITIONS are just the column
              grid, but no marks — a mark would assert activity on a turn. */}
          <div className="flex min-w-0 flex-1 items-center gap-[36px] overflow-hidden px-[10px]">
            {Array.from({ length: 12 }, (_, i) => (
              <span key={i} className="flex shrink-0 items-center gap-[6px]">
                <Bar w={1} h={9} dim={0.7} />
                <Bar w={14} h={7} dim={0.35} />
              </span>
            ))}
          </div>
        </div>
        {LANE_RAIL_ROWS.map((row, i) => (
          <div
            key={i}
            className="flex items-center border-b border-[color:var(--line-soft)] last:border-b-0"
            style={{
              height: ROW_H,
              // Group bands carry the same tint across the whole row in the real
              // plane, which is what makes the rail read as a tree of subjects
              // rather than a flat list.
              background: row.head ? "color-mix(in srgb, var(--control-bg) 60%, var(--bg))" : undefined,
            }}
          >
            <div
              className="flex shrink-0 items-center border-r border-[color:var(--line-soft)]"
              style={{ width: RAIL_W, height: ROW_H, paddingLeft: 8 + row.depth * 12, paddingRight: 8 }}
            >
              <Bar w={row.w} h={9} dim={row.head ? 0.6 : 0.4} />
            </div>
            <div className="min-w-0 flex-1" />
          </div>
        ))}
      </div>
    </div>
  );
}

function RawOutline() {
  return (
    <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[9px] py-[8px] min-w-0">
      {RAW_LINES.map((line, i) => (
        <div key={i} style={{ paddingLeft: line.indent }}>
          <Bar w={line.w} h={9} dim={0.4} />
        </div>
      ))}
    </div>
  );
}

const READING_WORD = "text-[11px] leading-[1.08] tracking-[0.06em] uppercase text-[color:var(--muted)]";

export default function TraceDetailSkeleton({ trace = null, pinnedFrom = null }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const headerSlot = useDetailHeaderSlot();
  const requested = searchParams.get("hist") || "turns";
  // A link to a reading the panel no longer has (Tree) loads Context, as the
  // panel does.
  const view = ["work", "lanes", "raw"].includes(requested) ? requested : "turns";
  const setView = (value) => {
    setSearchParams(() => {
      const next = readUrlParams();
      if (value && value !== "turns") next.set("hist", value);
      else next.delete("hist");
      return next;
    }, { replace: true });
  };
  // Raw is offered only for a trace that carries its request payload, which
  // is not known yet: it is in the switcher only when the link asked for it.
  const options = [
    ["turns", "Context"],
    ["work", "X-ray"],
    ["lanes", "Lanes"],
    ...(view === "raw" ? [["raw", "Raw"]] : []),
  ];

  // A deep-linked trace is seeded by makeLoadingTrace, which knows the id and
  // nothing else — so its `placeholder` seed contributes no header text. Opened
  // from the log, the row's own summary and timestamp are already correct and
  // survive the swap to the full payload without a reflow.
  const seeded = trace && !trace.placeholder ? trace : null;
  const sessionId = trace?.sessionId || trace?.id || null;

  // The switcher stays LIVE while the payload is in flight: it needs no data,
  // and a reader who opened the wrong reading should not have to wait for the
  // trace to arrive before correcting it. Workspace needs only the session's
  // id: the workspace reads the record itself.
  const switcher = (
    <ReadingSwitcherGroup
      options={options}
      active={view}
      onSelect={setView}
      onWorkspace={sessionId ? () => previewTraceCanvas({ id: sessionId, title: seeded?.summary || "" }) : null}
    />
  );

  const returnToOrigin = pinnedFrom && !pinnedFrom.isCurrent && pinnedFrom.onNavigate ? (
    <div>
      <button
        type="button"
        onClick={pinnedFrom.onNavigate}
        title={`Return to ${pinnedFrom.label}`}
        className="inline-flex items-center gap-[4px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[2px] text-[10px] leading-[1.1] text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)]"
      >
        <svg viewBox="0 0 12 12" className="h-[10px] w-[10px]" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M7 2L3 6l4 4" />
          <path d="M3 6h6" />
        </svg>
        <span className="truncate max-w-[160px]">{pinnedFrom.label}</span>
      </button>
    </div>
  ) : null;

  // Lanes is a takeover in the loaded panel -- the timeline owns the surface
  // and the switcher moves into the header, the summary beside it -- so its
  // placeholder is one too.
  if (view === "lanes") {
    const line = (
      <>
        {switcher}
        {seeded?.summary ? (
          <span data-header-summary="" className="min-w-0 truncate text-[12px] leading-[1.2] font-medium text-[color:var(--ink)] [flex:1_3_auto] @max-[640px]:hidden">
            {seeded.summary}
          </span>
        ) : null}
      </>
    );
    return (
      <div data-trace-skeleton="lanes" className="grid min-w-0 content-start gap-[8px]" aria-busy="true">
        {returnToOrigin}
        {headerSlot ? createPortal(line, headerSlot) : (
          <div className="flex min-w-0 items-center gap-[10px] border-b border-[color:var(--line-soft)] pb-[5px]">
            {switcher}
            {seeded?.summary ? (
              <span className="min-w-0 flex-1 truncate text-right text-[11px] leading-[1.2] text-[color:var(--muted)]">
                {seeded.summary}
              </span>
            ) : (
              <span className="flex min-w-0 flex-1 justify-end"><Bar w="40%" h={10} dim={0.4} /></span>
            )}
          </div>
        )}
        <LanesOutline />
      </div>
    );
  }

  return (
    <div data-trace-skeleton={view} className="grid gap-[10px] min-w-0" aria-busy="true">
      {returnToOrigin}
      {headerSlot ? createPortal(switcher, headerSlot) : null}
      {/* The summary line; the time only where no header byline carries it. */}
      <div className="grid gap-[3px] border-b border-[color:var(--line-soft)] pb-[6px] min-w-0">
        {seeded?.timestamp && !headerSlot ? (
          <span className="text-[11px] leading-[1.18] text-[color:var(--muted)]">{fullTimestampFormatter.format(seeded.timestamp)}</span>
        ) : null}
        <div className="flex min-w-0 items-center gap-[10px]">
          {seeded?.summary ? (
            <strong className="min-w-0 flex-1 text-[13px] leading-[1.3] font-medium text-[color:var(--ink)]">{seeded.summary}</strong>
          ) : (
            <div className="min-w-0 flex-1 py-[2px]"><Bar w="78%" h={13} dim={0.5} /></div>
          )}
          <LoadingNote className="shrink-0 text-[11px] leading-[1.18] text-[color:var(--muted)]" />
        </div>
      </div>
      {/* The review's disclosure row; its words wait on the review. */}
      <div aria-hidden="true" className="flex h-[24px] items-center justify-between gap-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[9px]">
        <Bar w={58} h={9} dim={0.5} />
        <Bar w={64} h={9} dim={0.35} />
      </div>
      <div className="grid gap-[8px] border-l border-[color:var(--line-soft)] pl-[12px] py-[2px] min-w-0">
        <span className={CONTEXT_EYEBROW_CLASS}>Trace metadata</span>
        <div className="grid gap-[8px] min-w-0">
          {META_ROWS.map((row, i) => (
            <div key={i} className="grid items-start gap-[10px] min-w-0 [grid-template-columns:80px_minmax(0,1fr)]">
              <Bar w={row.label} h={9} dim={0.45} className="mt-[3px]" />
              <div className="grid gap-[5px] min-w-0">
                {row.lines.map((l, j) => <Bar key={j} w={l.w} h={l.h} dim={l.dim ?? 0.6} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* The reading: its bar (headed by the switcher where the header offers
          no slot), then its outline. */}
      <div className="flex flex-col gap-[8px] pt-[2px] min-w-0">
        <div data-reading-switcher="" className="-mx-[1px] flex items-center justify-between gap-[8px] border-b border-[color:var(--line-soft)] px-[1px] py-[11px]">
          {headerSlot ? null : switcher}
          {view === "turns" ? (
            <div className="flex items-center gap-[10px]">
              <span className={READING_WORD}>Harness</span>
              <span className="text-[11px] leading-[1.08] text-[color:var(--line-strong)]">|</span>
              <span className={READING_WORD}>{(searchParams.get("ord") || "latest") === "latest" ? "Latest first" : "Earliest first"}</span>
            </div>
          ) : view === "raw" ? (
            <span className={READING_WORD}>Download JSON</span>
          ) : null}
        </div>
        {view === "work" ? <WorkOutline />
          : view === "raw" ? <RawOutline />
            : <TurnCards newestFirst={(searchParams.get("ord") || "latest") === "latest"} />}
      </div>
    </div>
  );
}
